import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, FileText, CheckCircle2, Send, Trash2, Edit2, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const NOTE_TYPES = [{ v: 'credit', l: 'Credit Note' }, { v: 'debit', l: 'Debit Note' }];
const PARTY_TYPES = [{ v: 'tenant', l: 'Tenant' }, { v: 'supplier', l: 'Supplier' }, { v: 'client', l: 'Client' }];
const LINK_TYPES = [
  { v: 'none', l: 'None (standalone)' },
  { v: 'invoice', l: 'Invoice' },
  { v: 'bill', l: 'Bill' },
  { v: 'lpo', l: 'LPO / Purchase Order' },
  { v: 'snag', l: 'Snag / Inspection' },
  { v: 'inspection', l: 'Inspection Report' },
];

const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-800 border border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 border border-rose-300',
};

const EMPTY = {
  note_type: 'credit',
  party_type: 'tenant',
  party_name: '',
  amount: '',
  description: '',
  link_type: 'none',
  link_ref: '',
  date: new Date().toISOString().slice(0, 10),
};

export default function CreditDebitNotes() {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState({ note_type: 'all', party_type: 'all', status: 'all' });

  const canApprove = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);
  const canWrite = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'].includes(user?.role);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.note_type !== 'all') params.note_type = filter.note_type;
      if (filter.party_type !== 'all') params.party_type = filter.party_type;
      if (filter.status !== 'all') params.status = filter.status;
      const r = await axios.get(`${API}/credit-debit-notes`, { params, withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
    setLoading(false);
  }, [filter]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openNew = () => { setEditingId(null); setDraft(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      note_type: r.note_type, party_type: r.party_type, party_name: r.party_name,
      amount: r.amount, description: r.description, link_type: r.link_type || 'none',
      link_ref: r.link_ref || '', date: r.date,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!draft.party_name?.trim()) return toast.error('Party name is required');
    if (!draft.description?.trim()) return toast.error('Description is required');
    const amt = parseFloat(draft.amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid lumpsum amount > 0');
    const payload = { ...draft, amount: amt };
    try {
      if (editingId) await axios.put(`${API}/credit-debit-notes/${editingId}`, payload, { withCredentials: true });
      else await axios.post(`${API}/credit-debit-notes`, payload, { withCredentials: true });
      toast.success('Saved');
      setOpen(false);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const submitForApproval = async (id) => {
    try {
      await axios.post(`${API}/credit-debit-notes/${id}/submit-for-approval`, {}, { withCredentials: true });
      toast.success('Sent for approval');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const approve = async (id) => {
    try {
      await axios.post(`${API}/credit-debit-notes/${id}/approve`, {}, { withCredentials: true });
      toast.success('Approved');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/credit-debit-notes/${id}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.note_number}?`)) return;
    try {
      await axios.delete(`${API}/credit-debit-notes/${r.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Layout>
      <div data-testid="notes-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <FileText className="w-7 h-7 text-amber-500" /> Credit / Debit Notes
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Lumpsum adjustments to a supplier, tenant or client account — optionally linked to an invoice / LPO / snag deduction.
              Flows into per-party statements once approved.
            </p>
          </div>
          {canWrite && (
            <Button data-testid="new-note-btn" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Plus className="w-4 h-4 mr-1" /> New Note
            </Button>
          )}
        </div>

        <Card className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filter.note_type} onValueChange={(v) => setFilter({ ...filter, note_type: v })}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="credit">Credit only</SelectItem>
                <SelectItem value="debit">Debit only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter.party_type} onValueChange={(v) => setFilter({ ...filter, party_type: v })}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parties</SelectItem>
                {PARTY_TYPES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
              <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Waiting for Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <div className="font-semibold">No credit / debit notes yet</div>
            <div className="text-sm">Click <b>New Note</b> to record your first adjustment.</div>
          </Card>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Note #</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Party</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">Linked</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-testid={`note-row-${r.id}`} className="border-t align-top hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{r.note_number}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.note_type === 'credit' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                        {r.note_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.date}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.party_name}</div>
                      <div className="text-[10px] text-muted-foreground capitalize">{r.party_type}</div>
                    </td>
                    <td className="px-3 py-2 max-w-xs">
                      <div className="line-clamp-2">{r.description}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.link_ref ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
                          {r.link_ref}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {r.currency} {Number(r.amount).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>
                        {r.status === 'pending_approval' ? 'Waiting' : r.status}
                      </span>
                      {r.status === 'rejected' && r.rejection_reason && (
                        <div className="text-[10px] text-rose-700 mt-0.5">{r.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 justify-end">
                        {r.status === 'draft' && canWrite && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {r.status === 'draft' && canWrite && (
                          <Button size="sm" variant="outline" onClick={() => submitForApproval(r.id)} data-testid={`submit-${r.id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {r.status === 'pending_approval' && canApprove && (
                          <>
                            <Button size="sm" onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white h-8">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => reject(r.id)} data-testid={`reject-${r.id}`} className="text-rose-700 h-8">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {canApprove && (
                          <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`del-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="note-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Note' : 'New Credit / Debit Note'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Note Type *</Label>
                  <Select value={draft.note_type} onValueChange={(v) => setDraft({ ...draft, note_type: v })}>
                    <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Party Type *</Label>
                  <Select value={draft.party_type} onValueChange={(v) => setDraft({ ...draft, party_type: v })}>
                    <SelectTrigger data-testid="party-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PARTY_TYPES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Party Name *</Label>
                  <Input data-testid="party-name-input" value={draft.party_name}
                    onChange={(e) => setDraft({ ...draft, party_name: e.target.value })}
                    placeholder={draft.party_type === 'tenant' ? 'e.g. Ahmed Al-Farsi' : draft.party_type === 'supplier' ? 'e.g. Reliable Maintenance LLC' : 'e.g. ABC Corp'} />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
                </div>
                <div>
                  <Label>Lumpsum Amount * (AED)</Label>
                  <Input data-testid="amount-input" type="number" min="0" step="0.01" value={draft.amount}
                    onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder="0.00" />
                </div>
                <div className="md:col-span-2">
                  <Label>Description * — what this amount is for</Label>
                  <Textarea data-testid="description-input" rows={2} value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder={draft.note_type === 'credit' ? 'e.g. Refund of overpaid parking deposit' : 'e.g. Return of defective paint — 2 cans'} />
                </div>
                <div>
                  <Label>Link Type (optional)</Label>
                  <Select value={draft.link_type} onValueChange={(v) => setDraft({ ...draft, link_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LINK_TYPES.map(l => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Link Reference #</Label>
                  <Input value={draft.link_ref} onChange={(e) => setDraft({ ...draft, link_ref: e.target.value })}
                    placeholder="e.g. INV-2025-0088, LPO-0007, INSP-2608-0002" disabled={draft.link_type === 'none'} />
                </div>
              </div>
              <div className="text-xs text-muted-foreground bg-slate-50 rounded p-2">
                <b>Impact:</b> {draft.note_type === 'credit'
                  ? draft.party_type === 'supplier'
                    ? 'Increases the amount payable to this supplier.'
                    : 'Reduces the receivable balance for this ' + draft.party_type + ' (a refund / discount).'
                  : draft.party_type === 'supplier'
                    ? 'Reduces the amount payable to this supplier (a return / claim).'
                    : 'Increases the receivable balance for this ' + draft.party_type + ' (an extra charge).'
                }
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="note-save-btn" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {editingId ? 'Update Note' : 'Create Note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
